import {
	EnvironmentContextProvider,
	LayoutProvider,
	MainNavigation,
	ThemeProvider,
	config,
	createCollateralLibraryRoutes,
	createLegalRoutes,
	createResourceRoutes,
	useLayoutContext,
	type DisplayTarget,
	type LayoutNavComponent,
	ResourceContextProvider,
	ResourceContextImpl,
	type MarketingSite,
} from '@ds-mgnl/marketing';
import type { LinkSourceProps, LinkSourceRecord } from '@dsx/react';
import { Provider as DsxProvider, useMediaQuery } from '@dsx/react';
import { forwardRef, useCallback } from 'react';
import {
	Outlet,
	Link as RouterLink,
	RouterProvider,
	createBrowserRouter,
} from 'react-router-dom';
import styles from './App.module.css';
import { DssiLogo, Footer, ctaLinks, topNavData } from './components';
import { useScrollManagement } from './hooks/useScrollManagement';
import { useUtmFormTracking } from './hooks/useUtmFormTracking';
import { ErrorPage } from './pages/ErrorPage';
import { MagnoliaPage } from './pages/MagnoliaPage';
import { magnoliaPageLoader } from './pages/magnoliaPageLoader';
import { getMagnoliaBaseDamUrl, getMagnoliaBaseUrl } from './util';

const linkSources = {
	default: forwardRef<HTMLAnchorElement, LinkSourceProps>((props, ref) => (
		<RouterLink ref={ref} {...props} to={props.href || ''} />
	)),
	external: forwardRef<HTMLAnchorElement, LinkSourceProps>((props, ref) => (
		<a ref={ref} {...props} />
	)),
	none: forwardRef<HTMLSpanElement, LinkSourceProps>((props, ref) => (
		<span ref={ref} {...props} />
	)),
} as LinkSourceRecord;

const SHOW_ON_SITE: MarketingSite = 'dssi';

const router = createBrowserRouter([
	{
		element: <RootLayout />,
		children: [
			...createResourceRoutes({
				errorElement: <ErrorPage />,
				mgnlBaseUrl: getMagnoliaBaseUrl() ?? '',
				resourcesHomeUrlPath: '/resources',
				pageConfig: config,
				showOnSite: SHOW_ON_SITE,
			}),
			...createLegalRoutes({
				errorElement: <ErrorPage />,
				mgnlBaseUrl: getMagnoliaBaseUrl() ?? '',
				legalHomeUrlPath: '/legal',
				pageConfig: config,
				showOnSite: SHOW_ON_SITE,
			}),
			...createCollateralLibraryRoutes({
				errorElement: <ErrorPage />,
				collateralLibUrlPath: '/collateral-library',
				mgnlBaseUrl: getMagnoliaBaseUrl() ?? '',
				mgnlBaseDamUrl: getMagnoliaBaseDamUrl() ?? '',
				mgnlCollateralHomePath: '/collateral-library',
				siteTitleSuffix: 'Direct Supply DSSI',
				showOnSite: SHOW_ON_SITE,
				pageConfig: config,
			}),
			{
				path: '*',
				element: <MagnoliaPage />,
				loader: magnoliaPageLoader,
				errorElement: <ErrorPage />,
			},
		],
	},
]);

const resourceContext = new ResourceContextImpl('/resources', SHOW_ON_SITE);

export function App() {
	useUtmFormTracking();
	const navigationComponent = useCallback<LayoutNavComponent>(
		({ transparent }) => {
			//
			const [isDesktop] = useMediaQuery('(min-width: 1120px)');
			const displayTypeOverride: DisplayTarget = isDesktop
				? 'desktop'
				: 'mobile';

			return (
				<MainNavigation
					transparent={transparent}
					navData={topNavData}
					navLogo={DssiLogo}
					ctaLinks={ctaLinks}
					displayTypeOverride={displayTypeOverride}
				/>
			);
		},
		[],
	);

	return (
		<DsxProvider locale="en-US" linkSources={linkSources}>
			<ThemeProvider theme="dssi">
				<ResourceContextProvider value={resourceContext}>
					<EnvironmentContextProvider
						value={{
							damHost: getMagnoliaBaseDamUrl() ?? '',
							appHost: getMagnoliaBaseUrl() ?? '',
							isInMagnolia: false,
						}}
					>
						<LayoutProvider initialNavigationComponent={navigationComponent}>
							<RouterProvider router={router} />
						</LayoutProvider>
					</EnvironmentContextProvider>
				</ResourceContextProvider>
			</ThemeProvider>
		</DsxProvider>
	);
}

function RootLayout() {
	useScrollManagement();
	const { renderNavigation } = useLayoutContext();

	return (
		<div className={styles['site-container']}>
			{renderNavigation({
				transparencyOverlapRatio: 1,
				transparencyOverlapType: 'vertical',
			})}
			<main>
				<Outlet />
			</main>
			<Footer />
		</div>
	);
}
